import { useCallback, useEffect, useState } from 'react';
import {
  getPage,
  initLivePreivew,
  onLiveEdit,
  isInLivePreviewIframe,
  Locale,
} from '@raisin/cp-utils';
import { PageTemplateProps } from 'types';
import { getReferences } from './getReferences';
import { addModulesListToPageContents } from './addModulesListToPageContents';
import { PublicProductsResponse, fetchPublicProducts } from 'api/dbs/fetchPublicProducts';
import { AnyLocale } from './parseAnyLocale';
import { createProductCardsTransformer } from 'components/ProductCards/utils';

const nestedRefs = getReferences();
const refs = Object.keys(nestedRefs).reduce((pre, key) => [...pre, ...nestedRefs[key]], []);
type ContentData = PageTemplateProps['contentData'];

export const useLivePreview = ({
  contentData,
  pageId,
  locale,
}: {
  contentData: ContentData;
  pageId: string | undefined;
  locale: string;
}) => {
  const [pageContent, setPageContent] = useState<ContentData>(contentData);
  const [dbs, setDbs] = useState<PublicProductsResponse>({ entries: [], total: 0 });

  const transformProductCards = useCallback(
    createProductCardsTransformer({ publicProducts: dbs }),
    [dbs],
  );

  const fetchContentData = useCallback(async () => {
    try {
      if (!pageId) {
        throw new Error('Page uid is null');
      }
      const newPage = await getPage({ locale, pageId, refs });

      const mapedContent = addModulesListToPageContents(newPage).content.map((content: any) => ({
        componentName: content._content_type_uid,
        componentData: content,
      }));

      const microseviceInjected = transformProductCards(mapedContent);
      setPageContent(microseviceInjected);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Live preview update failed');
    }
  }, [locale, pageId, transformProductCards]);

  const startLivePreview = useCallback(async () => {
    try {
      await initLivePreivew();
      onLiveEdit(fetchContentData);
    } catch (error) {
      if (error instanceof Error) {
        const { message } = error;
        setPageContent((pre) => [
          {
            componentData: { uid: 'live_preview', message, locale: locale as AnyLocale },
            componentName: 'internal_error',
          },
          ...pre.filter((content) => content.componentName !== 'internal_error'),
        ]);
      }
    }
  }, [fetchContentData, locale]);

  useEffect(() => {
    if (!isInLivePreviewIframe()) {
      return;
    }
    startLivePreview();
  }, [startLivePreview]);

  useEffect(() => {
    if (!isInLivePreviewIframe()) {
      return;
    }
    fetchPublicProducts({ locale: locale as Locale, headerId: 'useLivePreview' })
      .then(setDbs)
      // eslint-disable-next-line no-console
      .catch(console.error);
  }, [locale]);

  return pageContent;
};
